import { ChangeDetectionStrategy, Component, computed, ElementRef, Inject, input, Optional } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isExternalUrl } from '@examdojo/core/util/url-utils';
import { SMART_LINK_CONFIG, SmartLinkConfig } from '@examdojo/smart-link';
import { POPOVER_ID_DATA_ATTR, POPOVER_REGEX } from '@examdojo/tiptap-popover';
import escape from 'lodash/escape';
import { EMPTY, filter, fromEvent, merge, tap } from 'rxjs';
import { MarkdownModule } from 'ngx-markdown';
import { MarkdownBlock } from '../../../model';
import { POPOVER_TRIGGER_CLASS } from '../../inline-renderers/popover-trigger-host/popover-trigger-host.component';
import { generateHeadingId, HEADING_REGEX, TOC_ID_DATA_ATTR } from '../../table-of-contents';

@Component({
  selector: 'dojo-markdown-block-renderer',
  templateUrl: './markdown-block-renderer.component.html',
  styleUrl: './markdown-block-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MarkdownModule],
})
export class MarkdownBlockRendererComponent {
  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(SMART_LINK_CONFIG) private readonly smartLinkConfig: SmartLinkConfig | null,
  ) {
    this.interceptLinkClicks().pipe(takeUntilDestroyed()).subscribe();
  }

  private static readonly ICON_REGEX = /\{\{icon:(fa|img):([^:}]+)(?::([^:}]*))?(?::(bg))?\}\}/g;
  private static readonly COLOR_REGEX = /\{\{color:([^}]+)\}\}(.*?)\{\{\/color\}\}/g;

  readonly block = input.required<MarkdownBlock>();

  readonly katexOptions = {
    throwOnError: false,
    strict: 'warn' as const,
  };

  readonly processedMarkdown = computed(() => {
    const block = this.block();
    let markdown = block.markdown;
    markdown = this.addHeadingIds(markdown, block.id);
    markdown = this.transformPopoverSyntaxToHtml(markdown);
    markdown = this.transformIconSyntaxToHtml(markdown);
    markdown = this.transformColorSyntaxToHtml(markdown);
    return markdown;
  });

  /**
   * Adds data-toc-id spans before each heading for TOC navigation.
   * The span is placed before the heading so it can be used as a scroll target.
   */
  private addHeadingIds(markdown: string, blockId: string): string {
    let headingIndex = 0;
    return markdown.replace(HEADING_REGEX, (match) => {
      const id = generateHeadingId(blockId, headingIndex++);
      return `<span id="${id}" ${TOC_ID_DATA_ATTR}="${id}"></span>\n${match}`;
    });
  }

  /**
   * Transforms popover syntax {{popover:id}}content{{/popover}} into HTML spans
   * that render inline within the markdown content.
   */
  private transformPopoverSyntaxToHtml(markdown: string): string {
    return markdown.replace(POPOVER_REGEX, (_match, popoverId: string, content: string) => {
      return `<span class="${POPOVER_TRIGGER_CLASS}" ${POPOVER_ID_DATA_ATTR}="${escape(popoverId)}">${content}</span>`;
    });
  }

  /**
   * Transforms {{color:value}}text{{/color}} markers into colored spans.
   */
  private transformColorSyntaxToHtml(markdown: string): string {
    return markdown.replace(MarkdownBlockRendererComponent.COLOR_REGEX, (_match, color: string, content: string) => {
      return `<span style="color: ${escape(color)}">${content}</span>`;
    });
  }

  /**
   * Transforms {{icon:fa:name:color:bg}} markers into inline HTML.
   */
  private transformIconSyntaxToHtml(markdown: string): string {
    return markdown.replace(
      MarkdownBlockRendererComponent.ICON_REGEX,
      (_match, type: string, name: string, color?: string, bg?: string) => {
        if (type === 'fa') {
          const hasColor = color && color !== 'currentColor';
          if (bg) {
            const bgColor = MarkdownBlockRendererComponent.hexToRgba(color);
            const colorCss = hasColor ? `color: ${escape(color)}; ` : '';
            return `<span style="display: inline-flex; align-items: center; justify-content: center; width: 1.75em; height: 1.75em; border-radius: 6px; vertical-align: middle; ${colorCss}background-color: ${bgColor};"><i class="fas fa-${escape(name)}"></i></span>`;
          }
          const colorStyle = hasColor ? ` style="color: ${escape(color)}"` : '';
          return `<i class="fas fa-${escape(name)}"${colorStyle}></i>`;
        }
        if (type === 'img') {
          return `<img src="${escape(name)}" alt="" style="width: 1em; height: 1em; vertical-align: middle;" />`;
        }
        return '';
      },
    );
  }

  private static hexToRgba(color?: string): string {
    if (!color || color === 'currentColor') {
      return 'rgba(0, 0, 0, 0.06)';
    }
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.15)`;
  }

  /**
   * Intercepts clicks on rendered markdown `<a>` tags and routes non-external links
   * through the SmartLinkConfig resolver instead of navigating directly.
   * Also handles middle-click (auxclick) and passes modifier state via `newTab` context.
   */
  private interceptLinkClicks() {
    if (!this.smartLinkConfig?.resolve) {
      return EMPTY;
    }

    const el = this.elementRef.nativeElement;

    const resolveLink = (event: MouseEvent, newTab: boolean) => {
      const anchor = (event.target as HTMLElement).closest('a');
      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || isExternalUrl(href)) {
        return;
      }

      event.preventDefault();
      this.smartLinkConfig?.resolve(href, { url: window.location.href, newTab });
    };

    const click$ = fromEvent<MouseEvent>(el, 'click').pipe(
      tap((event) => resolveLink(event, event.metaKey || event.ctrlKey)),
    );

    const auxClick$ = fromEvent<MouseEvent>(el, 'auxclick').pipe(
      filter((event) => event.button === 1),
      tap((event) => resolveLink(event, true)),
    );

    return merge(click$, auxClick$);
  }
}
